import React, { useEffect, useState, useRef } from "react";
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  useGridSelector,
  Toolbar,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  gridDensitySelector,
  useGridApiContext,
  gridSortedRowIdsSelector,
  ToolbarButton,
  gridClasses,
} from "@mui/x-data-grid";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import { alpha, styled } from "@mui/material/styles";
import { useContextProvider } from "../ContextProvider";
import { useOktaAuth } from "@okta/okta-react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import axios from "axios";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckIcon from '@mui/icons-material/Check';
import { getSupplierSetupTableColumns } from "./adapter";

export const SupplierSetupTable = (props) => {
  const {
    searchValue,
    setSearchValue,
    noTpData,
    setNoTpData,
    gridTPData,
    setGridTPData,
    ediType,
  } = useContextProvider();
  const { oktaAuth } = useOktaAuth();
  const [loading, isLoading] = useState(false);
  const token = oktaAuth.getAccessToken();

  const ODD_OPACITY = 0.2;
  const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: theme.palette.grey[100],
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
      "&.Mui-selected": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity
        ),
        "&:hover, &.Mui-hovered": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY +
              theme.palette.action.selectedOpacity +
              theme.palette.action.hoverOpacity
          ),
          "@media (hover: none)": {
            backgroundColor: alpha(
              theme.palette.primary.main,
              ODD_OPACITY + theme.palette.action.selectedOpacity
            ),
          },
        },
      },
    },
    "& .cell": {
      borderTop: "1px solid rgba(204,204,204,1)",
    },
  }));
const DENSITY_OPTIONS = [
  { label: 'Compact density', value: 'compact' },
  { label: 'Standard density', value: 'standard' },
  { label: 'Comfortable density', value: 'comfortable' },
];
  const getUnfilteredRows = ({ apiRef }) => gridSortedRowIdsSelector(apiRef);

  const buttonBaseProps = {
    size: "small",
    startIcon: <fuel-icon name={"microsoft_excel"} />,
  };

    const CustomToolbar=()=> {
        const apiRef = useGridApiContext();
        const density = useGridSelector(apiRef, gridDensitySelector);
        const [densityMenuOpen, setDensityMenuOpen] = useState(false);
        const densityMenuTriggerRef = useRef(null);

        const handleExport = (options) => apiRef.current.exportDataAsCsv(options);
        return(
            <Toolbar>
                <ColumnsPanelTrigger  >
                    <ViewColumnIcon fontSize="small" />
                    Columns
                </ColumnsPanelTrigger>
                <FilterPanelTrigger >
                    <FilterListIcon fontSize="small"/>
                    Filters
                </FilterPanelTrigger>

                <Tooltip title="Settings">
                  <Button
                    ref={densityMenuTriggerRef}
                     sx={{
                        borderColor: "green",
                      }}
                    id="density-menu-trigger"
                    aria-controls="density-menu"
                    aria-haspopup="true"
                    aria-expanded={densityMenuOpen ? 'true' : undefined}
                    onClick={() => setDensityMenuOpen(true)}
                  >
                    <fuel-icon name="menu" />Density
                  </Button>
                </Tooltip>

                <Menu
                  id="density-menu"
                  anchorEl={densityMenuTriggerRef.current}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={densityMenuOpen}
                  onClose={() => setDensityMenuOpen(false)}
                  slotProps={{
                    list: {
                      'aria-labelledby': 'density-menu-trigger',
                    },
                  }}
                >
                  {DENSITY_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => {
                        apiRef.current.setDensity(option.value);
                        setDensityMenuOpen(false);
                      }}
                    >
                      <ListItemIcon>
                        {density === option.value && <CheckIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText>{option.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
                
                <Button
                    {...buttonBaseProps}

                    onClick={() => handleExport({ getRowsToExport: getUnfilteredRows })}
                >
                    Download
                </Button>

            </Toolbar>
        )
    }

  useEffect(() => {
    const fetchTploadData = async (queryString, notFoundMessage) => {
      await axios
        .get(
          process.env.REACT_APP_EDI_SETUP_DETAILS_BASE_URL +
            "/api/tpload/list?" +
            queryString,
          {
            headers: {
              Authorization: "Bearer " + token,
            },
          }
        )
        .then((response) => {
          setGridTPData(response?.data);
          if (response?.data.length === 0) {
            setNoTpData("No EDI Setup Found for: " + notFoundMessage);
          }
        })
        .catch((error) => {
          setNoTpData(
            "An unexpected error ocurred while processing your request, please try again"
          );
          setGridTPData([]);
          console.log(error);
        });
    };

    if (searchValue.length >= 0) {
      const searchCriterias = [...searchValue, ...ediType];
      if (searchCriterias.length >= 1) {
        const queryString = searchCriterias
          .map((element) => {
            return element.type.toLowerCase() + "=" + element.code;
          })
          .join("&");
        const notFoundMessage = searchCriterias
          .map((element) => {
            return element.type.replace("_", " ") + "='" + element.code + "'";
          })
          .join(" and ");
        isLoading(true);
        fetchTploadData(queryString, notFoundMessage).then(() => {
          isLoading(false);
        });
      } else {
        setGridTPData([]);
        setNoTpData("Specify a criteria first");
      }
    }
  }, [searchValue, ediType]);

  return (
    <div style={{ height: "50vh", width: "100%" }}>
      <StripedDataGrid
        rows={gridTPData}
        columns={getSupplierSetupTableColumns()}
        slots={{
                    toolbar: CustomToolbar,
                }}
        loading={loading}
        pageSize={100}
        filterOptions={(x) => x}
        sx={{
          borderColor: "rgb(204, 204, 204)",
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        getCellClassName={() => "cell"}
        showToolbar
        onRowClick={(ids) => {
          const selectedRow = gridTPData.filter((item) => item.id === ids.id);
          props.handlerDetails(selectedRow[0]);
        }}
      />
    </div>
  );
};
